import { useMemo } from "react";

import { Permission } from "../../../utils/types/documents.type";
import { DataGridColDef } from "../../../utils/types/listItems";
import {
  FileSizeColDef,
  FileStatusColDef,
  FolderNameColDef,
  FundColDef,
  InvestorColDef,
  NameColDef,
  PermissionsColDef,
  QuarterColDef,
  WaterMarkColDef,
} from "../components/grid/columns/ColumnDef";
import { DocumentsState } from "../types";

type HeaderListProps = {
  handleOnChipClick: () => void;
  isEditable: boolean;
  isAdmin: boolean;
  viewConfig: Record<string, any> | null | undefined;
  nameColumnWidth?: number;
};

const none = {
  key: "(none)",
  name: "(none)",
};

type None = {
  key: string;
  name: string;
};

function useDocumentsGridHeaders(
  state: DocumentsState,
  {
    handleOnChipClick = () => { },
    isEditable = false,
    isAdmin = true,
    viewConfig,
    nameColumnWidth
  }: HeaderListProps
): Array<DataGridColDef> {

  const rootSelected = state.cursor?.length === 0;

  const { funds, investors, quarters } = state.filtersData;
  const selectedInvestors = state.selectedFilters.investors;
  const selectedFunds = state.selectedFilters.funds;
  const selectedQuarters = state.selectedFilters.quarters;
  const permissions: Array<Permission | None> | null | undefined = [
    ...(state.permissions || []),
    none,
  ];
  const selectedPermissions = state.selectedPermissionsFilter;
  const searchText = state.selectedFilters.searchText;
  const showFolders = state.selectedFilters.showFolders;
  const sort = state.sort;
  const names = [
    ...(state.currentFolder?.folders?.map((folder) => ({
      name: folder.name,
      id: folder.id,
    })) || []),
    ...(state.currentFolder?.publishedFiles?.files?.map((file) => ({
      name: file.name,
      id: file.id,
    })) || []),
    ...(state.currentFolder?.unPublishedFiles?.files?.map((file) => ({
      name: file.name,
      id: file.id,
    })) || []),
  ];
  const selectedNames: Array<string> = state.selectedNameFilter;

  const rootHeadersArray: Array<DataGridColDef> = [FolderNameColDef];

  let childHeadersArray: Array<DataGridColDef> = [
    {
      ...NameColDef,
      inlineToggleSwitch: showFolders,
      inlineFilterOptions: names,
      inlineFilterSelected: selectedNames,
    },
    {
      ...InvestorColDef,
      inlineFilterOptions: investors,
      inlineFilterSelected: selectedInvestors,
    },
    {
      ...FundColDef,
      inlineFilterOptions: funds,
      inlineFilterSelected: selectedFunds,
    },
    {
      ...QuarterColDef,
      inlineFilterOptions: quarters,
      inlineFilterSelected: selectedQuarters,
    },
    FileSizeColDef,
  ];

  if (isAdmin) {
    rootHeadersArray.push(
      isEditable
        ? {
          ...PermissionsColDef,
          onChipClick: handleOnChipClick,
          inlineFilterOptions: permissions,
          inlineFilterSelected: selectedPermissions,
        }
        : {
          ...PermissionsColDef,
          inlineFilterOptions: permissions,
          inlineFilterSelected: selectedPermissions,
        }
    );
    childHeadersArray.push(FileStatusColDef);
  }

  if (isEditable) {
    rootHeadersArray.push(WaterMarkColDef);
  }

  if (viewConfig?.viewItems) {
    childHeadersArray = childHeadersArray.map((item) => {
      const column = viewConfig.viewItems.find(
        (column: any) => item.field === column.code
      );

      return {
        ...item,
        index: column.order ?? item.index,
        hide: !(column.visible ?? item.hide),
      };
    });

    childHeadersArray = childHeadersArray.sort((a, b) =>
      a.index > b.index ? 1 : -1
    );
  }

  const rootLevelHeaders: Array<DataGridColDef> = useMemo(
    () => rootHeadersArray,
    [showFolders, sort, selectedPermissions, permissions]
  );

  const childLevelHeaders: Array<DataGridColDef> = useMemo(
    () => childHeadersArray,
    [
      sort,
      showFolders,
      JSON.stringify(selectedNames),
      JSON.stringify(selectedInvestors),
      JSON.stringify(selectedFunds),
      JSON.stringify(selectedQuarters)
    ]
  );

  return useMemo(() => {
    if (rootSelected) {
      return rootLevelHeaders;
    }

    childLevelHeaders[0].width = nameColumnWidth;
    return childLevelHeaders;
  }, [rootSelected, rootLevelHeaders, childLevelHeaders]);
}

export default useDocumentsGridHeaders;
